/**
 * RasterToolCard — Card 2 from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.2.
 *
 * Surfaces every raster control listed in the masterplan: opacity, band
 * selection, color ramp, brightness/contrast/saturation, no-data mask,
 * blending, histogram link (`HistogramPanel`), pixel inspector pick row
 * (`RasterPixelPickRow`), clip/mask placeholder, profile-sampling chip
 * for elevation-like rasters, and a metadata / provenance card.
 *
 * The card activates when `useViewerStore.sceneSelection.toolCardKind`
 * resolves to `'raster'`. It emits its mutations through
 * `setRasterToolCard` so an adapter mock or test can assert on a stable
 * `RasterToolCardState` shape without touching MapLibre internals.
 *
 * Design-system compliance: tokens only, Material Symbols rounded only,
 * 40 px minimum touch target on every chip / slider thumb / button, one
 * primary CTA per action row.
 */

import { useCallback, useMemo } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  Banner,
  Callout,
  Chip,
  tokens,
  type ChipVariant,
} from '@/design';
import {
  ToolCardSection,
  ToolCardShell,
  ToolCardSliderRow,
  ToolCardToggleRow,
} from '@/components/toolcards/ToolCardShell';
import {
  useViewerStore,
  type RasterBlendMode,
  type RasterColorRamp,
} from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';
import { HistogramPanel } from './HistogramPanel';
import { RasterPixelPickRow } from './RasterPixelPickRow';

export type RasterTileHealth =
  | 'rest'
  | 'loading'
  | 'partial'
  | 'failed'
  | 'stale'
  | 'permission-denied';

export interface RasterToolCardProps {
  /** The active raster layer; carries the metadata/provenance facts. */
  layer?: Layer | null;
  /** Tile health for the loading/stale/error banner row. */
  tileHealth?: RasterTileHealth;
  /** Permission-denied banner copy (overrides `tileHealth`). */
  permissionDenied?: string | null;
  /** Read-only flag from the gTwin permission model. */
  readOnly?: boolean;
  /** When the active raster is elevation-like, expose profile sampling. */
  isElevationLike?: boolean;
  /** Click-to-pick clip / mask geometry (placeholder). */
  onPickClipMask?: () => void;
  /** Click-to-open profile sampling panel. */
  onOpenProfileSampling?: () => void;
  /** Force render outside the tool-card kind gate (for tests / preview). */
  forceVisible?: boolean;
  'data-testid'?: string;
}

const COLOR_RAMPS: readonly { id: RasterColorRamp; label: string }[] = [
  { id: 'viridis', label: 'Viridis' },
  { id: 'magma', label: 'Magma' },
  { id: 'inferno', label: 'Inferno' },
  { id: 'plasma', label: 'Plasma' },
  { id: 'turbo', label: 'Turbo' },
  { id: 'terrain', label: 'Terrain' },
  { id: 'RdBu', label: 'RdBu' },
  { id: 'greyscale', label: 'Greyscale' },
];

const BLEND_MODES: readonly { id: RasterBlendMode; label: string }[] = [
  { id: 'normal', label: 'Normal' },
  { id: 'multiply', label: 'Multiply' },
  { id: 'screen', label: 'Screen' },
  { id: 'overlay', label: 'Overlay' },
];

const TILE_HEALTH_TO_CHIP: Record<
  RasterTileHealth,
  { label: string; variant: ChipVariant; icon: 'check_circle' | 'pending' | 'sync_problem' | 'error' | 'lock' }
> = {
  rest: { label: 'Tiles ready', variant: 'success', icon: 'check_circle' },
  loading: { label: 'Loading tiles', variant: 'info', icon: 'pending' },
  partial: { label: 'Partial tiles', variant: 'warning', icon: 'sync_problem' },
  failed: { label: 'Tile load failed', variant: 'error', icon: 'error' },
  stale: { label: 'Stale tiles', variant: 'warning', icon: 'sync_problem' },
  'permission-denied': {
    label: 'Permission required',
    variant: 'warning',
    icon: 'lock',
  },
};

function formatPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function formatMultiplier(v: number): string {
  return `${v.toFixed(2)}×`;
}

function bandIndexInputToValue(raw: string): number {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(16, parsed));
}

export function RasterToolCard({
  layer,
  tileHealth = 'rest',
  permissionDenied,
  readOnly,
  isElevationLike,
  onPickClipMask,
  onOpenProfileSampling,
  forceVisible,
  'data-testid': testId,
}: RasterToolCardProps) {
  const toolCardKind = useViewerSlice(
    (s) => s.sceneSelection.toolCardKind ?? 'gtwin',
  );
  const state = useViewerSlice((s) => s.rasterToolCard);
  const setRasterToolCard = useViewerStore((s) => s.setRasterToolCard);

  const tileChip = TILE_HEALTH_TO_CHIP[tileHealth];

  const updateBand = useCallback(
    (channelIdx: 0 | 1 | 2, nextValue: number) => {
      const bands: [number, number, number] = [...state.bands];
      bands[channelIdx] = nextValue;
      setRasterToolCard({ bands });
    },
    [state.bands, setRasterToolCard],
  );

  const provenanceFacts = useMemo(() => {
    const facts: { label: string; value: string }[] = [];
    if (layer?.label) facts.push({ label: 'Layer', value: layer.label });
    if (layer?.id) facts.push({ label: 'Scene item', value: layer.id });
    if (layer?.kind) facts.push({ label: 'Kind', value: layer.kind });
    if (layer?.crs) facts.push({ label: 'CRS', value: layer.crs });
    if (layer?.bbox && layer.bbox.length === 4) {
      facts.push({
        label: 'BBox',
        value: layer.bbox.map((c) => c.toFixed(4)).join(', '),
      });
    }
    return facts;
  }, [layer]);

  const visible = forceVisible || toolCardKind === 'raster';
  if (!visible) return null;

  const showPermissionBanner = !!permissionDenied;
  const showTileChip = tileHealth !== 'rest';

  return (
    <ToolCardShell
      icon="image"
      title="Raster / COG"
      subtitle={layer?.label ?? layer?.id ?? null}
      data-testid={testId ?? 'raster-toolcard'}
      topBanners={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
          }}
        >
          {showPermissionBanner ? (
            <Banner
              severity="warning"
              title="Raster restricted"
              body={permissionDenied}
              narrationKey="raster-toolcard-permission"
            />
          ) : null}
          {readOnly ? (
            <Chip icon="visibility" variant="tertiary" label="Read-only" />
          ) : null}
          {showTileChip ? (
            <Chip
              icon={tileChip.icon}
              variant={tileChip.variant}
              label={tileChip.label}
            />
          ) : null}
        </div>
      }
      footer={
        provenanceFacts.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: `${tokens.space[1]}px ${tokens.space[2]}px`,
            }}
          >
            {provenanceFacts.map((fact) => (
              <div
                key={fact.label}
                style={{
                  display: 'contents',
                  fontFamily: tokens.font.family.mono,
                }}
              >
                <span style={{ color: tokens.color.ink.secondary }}>
                  {fact.label}
                </span>
                <span
                  style={{
                    fontFamily: tokens.font.family.mono,
                    color: tokens.color.ink.primary,
                  }}
                >
                  {fact.value}
                </span>
              </div>
            ))}
          </div>
        ) : null
      }
    >
      <ToolCardSection title="Display">
        <ToolCardSliderRow
          label="Opacity"
          icon="tune"
          min={0}
          max={1}
          step={0.05}
          value={state.opacity}
          readout={formatPercent(state.opacity)}
          onChange={(next) => setRasterToolCard({ opacity: next })}
          data-testid="raster-opacity-slider"
        />
        <ToolCardSliderRow
          label="Brightness"
          icon="flare"
          min={0.5}
          max={1.5}
          step={0.05}
          value={state.brightness}
          readout={formatMultiplier(state.brightness)}
          onChange={(next) => setRasterToolCard({ brightness: next })}
          data-testid="raster-brightness-slider"
        />
        <ToolCardSliderRow
          label="Contrast"
          icon="contrast"
          min={0.5}
          max={1.5}
          step={0.05}
          value={state.contrast}
          readout={formatMultiplier(state.contrast)}
          onChange={(next) => setRasterToolCard({ contrast: next })}
          data-testid="raster-contrast-slider"
        />
        <ToolCardSliderRow
          label="Saturation"
          icon="colorize"
          min={0}
          max={2}
          step={0.05}
          value={state.saturation}
          readout={formatMultiplier(state.saturation)}
          onChange={(next) => setRasterToolCard({ saturation: next })}
          data-testid="raster-saturation-slider"
        />
      </ToolCardSection>

      <ToolCardSection
        title="Bands"
        description="Pick the R / G / B band indices (1-based) for the TiTiler renderer."
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: tokens.space[2],
          }}
        >
          {(['R', 'G', 'B'] as const).map((channel, idx) => (
            <label
              key={channel}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
              }}
            >
              {channel}
              <input
                type="number"
                min={1}
                max={16}
                value={state.bands[idx as 0 | 1 | 2]}
                aria-label={`${channel} band index`}
                data-testid={`raster-band-${channel.toLowerCase()}`}
                onChange={(e) =>
                  updateBand(
                    idx as 0 | 1 | 2,
                    bandIndexInputToValue(e.currentTarget.value),
                  )
                }
                style={{
                  appearance: 'none',
                  width: '100%',
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  fontFamily: tokens.font.family.mono,
                  fontSize: tokens.font.role.engMono.size,
                }}
              />
            </label>
          ))}
        </div>
      </ToolCardSection>

      <ToolCardSection title="Color ramp">
        <div
          role="radiogroup"
          aria-label="Color ramp"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[1],
          }}
        >
          {COLOR_RAMPS.map((ramp) => (
            <Chip
              key={ramp.id}
              icon="palette"
              variant={state.colorRamp === ramp.id ? 'info' : 'neutral'}
              label={ramp.label}
              pressable
              pressed={state.colorRamp === ramp.id}
              onClick={() => setRasterToolCard({ colorRamp: ramp.id })}
              data-testid={`raster-ramp-${ramp.id}`}
            />
          ))}
        </div>
      </ToolCardSection>

      <ToolCardSection title="Blending & no-data">
        <ToolCardToggleRow
          label="No-data mask"
          description="Hide pixels that match the raster's no-data sentinel."
          icon="block"
          checked={state.noDataMask}
          onChange={(next) => setRasterToolCard({ noDataMask: next })}
          data-testid="raster-nodata-toggle"
        />
        <div
          role="radiogroup"
          aria-label="Blend mode"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[1],
          }}
        >
          {BLEND_MODES.map((mode) => (
            <Chip
              key={mode.id}
              icon="gradient"
              variant={state.blending === mode.id ? 'info' : 'neutral'}
              label={mode.label}
              pressable
              pressed={state.blending === mode.id}
              onClick={() => setRasterToolCard({ blending: mode.id })}
              data-testid={`raster-blend-${mode.id}`}
            />
          ))}
        </div>
      </ToolCardSection>

      <ToolCardSection title="Pixel inspector">
        <RasterPixelPickRow />
      </ToolCardSection>

      <ToolCardSection title="Histogram & statistics">
        <HistogramPanel />
      </ToolCardSection>

      <ToolCardSection title="Sampling & clip">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
          }}
        >
          {isElevationLike ? (
            <button
              type="button"
              onClick={onOpenProfileSampling}
              disabled={!onOpenProfileSampling}
              aria-label="Open elevation profile sampling"
              data-testid="raster-open-profile"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
                minHeight: tokens.density.touchMin,
                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                background: tokens.color.surface[1],
                color: tokens.color.ink.primary,
                border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                borderRadius: tokens.radius.input,
                cursor: onOpenProfileSampling ? 'pointer' : 'default',
                opacity: onOpenProfileSampling ? 1 : 0.5,
              }}
            >
              Profile sampling
            </button>
          ) : null}
          <button
            type="button"
            onClick={onPickClipMask}
            disabled={readOnly || !onPickClipMask}
            aria-label="Pick clip or mask geometry"
            data-testid="raster-pick-clip-mask"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: !readOnly && onPickClipMask ? 'pointer' : 'default',
              opacity: !readOnly && onPickClipMask ? 1 : 0.5,
            }}
          >
            Clip / mask…
          </button>
        </div>
        {!onPickClipMask ? (
          <Callout severity="info" icon="info">
            Clip / mask by geometry will land alongside vector ToolCard
            (WP-4) so it can pick a selection from a feature layer.
          </Callout>
        ) : null}
      </ToolCardSection>
    </ToolCardShell>
  );
}
